import { Box, Stack, Typography } from '@mui/material'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import Page from '@/components/layouts/Page'
import { ConfigVersionInfo } from '@/features/controller/configuration/components/ConfigVersionInfo'
import { ConfigVersionNavigationLinks } from '@/features/controller/configuration/components/ConfigVersionNavigationLinks'
import { DiffViewer } from '@/features/controller/configuration/components/textEditor/DiffViewer'
import { useControllerConfigVersionQuery } from '@/features/controller/configuration/hooks/useControllerConfigVersionQuery'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'
import { renderConfigVersionName } from '@/features/controller/configuration/utils/versionNames'
import { ControllerRouteInformation } from '@/features/controller/constants'

interface VersionDisplayProps {
  label: string
  value: string
}

function VersionDisplay({ label, value }: Readonly<VersionDisplayProps>) {
  return (
    <Stack spacing={0.5} sx={{ width: 'fit-content' }}>
      <Typography color="common.white" fontWeight={400} variant="caption">
        {label}
      </Typography>

      <Box
        sx={{
          color: 'common.white',
          border: '1px solid white',
          borderRadius: 1,
          px: 2,
          py: 1,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value}
      </Box>
    </Stack>
  )
}

export function ConfigVersionDiffPage() {
  const { controllerId, versionId, prevVersionId } = useParams()
  const { t, i18n } = useTranslation()

  if (!controllerId || !versionId || !prevVersionId) {
    return null
  }

  const { configVersion, isLoading } = useControllerConfigVersionQuery(versionId)
  const { configVersion: prevConfigVersion, isLoading: isPreviousLoading } =
    useControllerConfigVersionQuery(prevVersionId)

  if (isLoading || isPreviousLoading) {
    return <FullPageSpinner />
  }

  if (!configVersion || !prevConfigVersion) {
    return null
  }

  const configType = getControllerConfigType(configVersion.configurationTypeId)

  // If we are already showing the previous version on the left, then don't show links to it
  const showLinksToPrevious = prevConfigVersion.id !== configVersion.previousVersionId

  return (
    <Page
      pageHeaderProps={{
        pageTitle: `${configType.name} ${t('controllers.configuration.version.version_comparison')}`,
        breadcrumbItems: [
          {
            text: t('controllers.controller'),
            to: ControllerRouteInformation.CONTROLLER_DETAILS.navigationPath(controllerId),
          },
          {
            text: configType.name,
            to: ControllerRouteInformation.CONTROLLER_CONFIG_TYPED.navigationPath(controllerId, configType.id),
          },
        ],
        appBarContent: (
          <Stack direction="row" spacing={2} sx={{ m: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <VersionDisplay
                label={t('controllers.configuration.version.original_version')}
                value={renderConfigVersionName(prevConfigVersion, i18n.language)}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <VersionDisplay
                label={t('controllers.configuration.version.modified_version')}
                value={renderConfigVersionName(configVersion, i18n.language)}
              />
            </Box>
          </Stack>
        ),
      }}
    >
      <Paper sx={{ p: 3, mb: 2 }}>
        <DiffViewer modifiedText={configVersion.value} originalText={prevConfigVersion.value} />
      </Paper>

      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <ConfigVersionInfo configVersion={prevConfigVersion} controllerId={controllerId} />
          {prevConfigVersion.previousVersionId && (
            <ConfigVersionNavigationLinks
              configVersionId={prevConfigVersion.id}
              controllerId={controllerId}
              previousVersionId={prevConfigVersion.previousVersionId}
            />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
          {showLinksToPrevious && configVersion.previousVersionId && (
            <ConfigVersionNavigationLinks
              configVersionId={configVersion.id}
              controllerId={controllerId}
              previousVersionId={configVersion.previousVersionId}
            />
          )}
        </Box>
      </Stack>
    </Page>
  )
}
